import { useState } from "react";
import api from "../lib/api";
import { Search, Loader2, AlertTriangle, TrendingUp, Target, Zap, Sparkles, Shield } from "lucide-react";
import { toast } from "sonner";

function tryParse(raw) {
  if (!raw) return null;
  let txt = raw.trim();
  if (txt.startsWith("```")) txt = txt.replace(/^```(json)?/i, "").replace(/```$/, "").trim();
  try { return JSON.parse(txt); } catch {
    const m = txt.match(/\{[\s\S]*\}/);
    if (m) { try { return JSON.parse(m[0]); } catch {} }
    return null;
  }
}

export default function CompetitorIntel() {
  const [form, setForm] = useState({ handle: "", platform: "instagram", niche: "", observations: "" });
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const analyze = async () => {
    if (!form.handle) { toast.error("Renseigne un handle"); return; }
    setLoading(true); setData(null);
    try {
      const r = await api.aiCompetitorAnalyze(form);
      const parsed = tryParse(r.raw);
      if (!parsed) { toast.error("Réponse IA invalide — réessaie"); return; }
      setData(parsed);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur IA"); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-7 animate-fadeUp" data-testid="competitor-intel-page">
      <header className="border-l-2 border-[#CCFF00] pl-5">
        <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#CCFF00] mb-2">// COMPETITOR INTELLIGENCE</div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold text-white tracking-tight">Espionnage stratégique</h1>
        <p className="text-zinc-400 mt-2 max-w-2xl">Analyse profonde IA d'un concurrent : piliers, hooks signature, patterns viraux, angles à voler.</p>
      </header>

      <div className="border border-[#1E1E24] bg-[#0A0A0C] rounded-sm p-5 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <input data-testid="ci-handle" placeholder="@handle" value={form.handle} onChange={e => setForm({ ...form, handle: e.target.value })}
            className="bg-[#070708] border border-[#1E1E24] rounded-sm px-3 py-2 text-sm text-white focus:border-[#CCFF00]/50 outline-none" />
          <select data-testid="ci-platform" value={form.platform} onChange={e => setForm({ ...form, platform: e.target.value })}
            className="bg-[#070708] border border-[#1E1E24] rounded-sm px-3 py-2 text-sm text-white focus:border-[#CCFF00]/50 outline-none">
            {["instagram", "tiktok", "youtube", "x", "linkedin"].map(p => <option key={p}>{p}</option>)}
          </select>
          <input data-testid="ci-niche" placeholder="Niche (fitness, finance...)" value={form.niche} onChange={e => setForm({ ...form, niche: e.target.value })}
            className="bg-[#070708] border border-[#1E1E24] rounded-sm px-3 py-2 text-sm text-white focus:border-[#CCFF00]/50 outline-none" />
        </div>
        <textarea data-testid="ci-obs" rows={3} placeholder="(Optionnel) Observations : top posts vus, formats, hooks repérés..."
          value={form.observations} onChange={e => setForm({ ...form, observations: e.target.value })}
          className="w-full bg-[#070708] border border-[#1E1E24] rounded-sm px-3 py-2 text-sm text-white focus:border-[#CCFF00]/50 outline-none" />
        <button data-testid="ci-analyze" onClick={analyze} disabled={loading}
          className="bg-[#CCFF00] text-black font-medium px-5 py-2.5 rounded-sm hover:bg-[#B3E600] disabled:opacity-50 flex items-center gap-2 text-sm glow-volt">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" strokeWidth={2} />}
          {loading ? "Analyse en cours..." : "Lancer l'analyse"}
        </button>
      </div>

      {data && (
        <div className="space-y-5" data-testid="ci-result">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card title="Persona" body={data.persona} icon={Target} />
            <Card title="Positionnement" body={data.positioning} icon={Zap} />
            <div className="border border-[#1E1E24] bg-[#0A0A0C] rounded-sm p-5 grid grid-cols-2 gap-3">
              <Score label="Menace" value={data.threat_score} color="#EF4444" />
              <Score label="Opportunité" value={data.opportunity_score} color="#CCFF00" />
            </div>
          </div>

          <Section title="Top 3 hooks signature" icon={Zap}>
            <ol className="space-y-2">
              {(data.top_3_hooks || []).map((h, i) => (
                <li key={i} className="flex items-start gap-3 border border-[#1E1E24] bg-[#0A0A0C] p-3 rounded-sm">
                  <span className="font-mono text-[#CCFF00] text-sm">#{i + 1}</span>
                  <span className="text-zinc-200 text-sm">{h}</span>
                </li>
              ))}
            </ol>
          </Section>

          <Section title="Piliers de contenu" icon={Sparkles}>
            <div className="space-y-2">
              {(data.content_pillars || []).map((p, i) => (
                <div key={i} className="border border-[#1E1E24] bg-[#0A0A0C] p-4 rounded-sm">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-white font-medium">{p.name}</div>
                    <div className="font-mono text-xs text-[#CCFF00]">{p.weight_pct}%</div>
                  </div>
                  <div className="h-1.5 bg-[#1E1E24] rounded-full overflow-hidden mb-2">
                    <div className="h-full bg-[#CCFF00]" style={{ width: `${p.weight_pct}%` }} />
                  </div>
                  {p.examples && <div className="text-xs text-zinc-500 leading-relaxed">{(p.examples || []).join(" · ")}</div>}
                </div>
              ))}
            </div>
          </Section>

          <Section title="Patterns viraux" icon={TrendingUp}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {(data.viral_patterns || []).map((p, i) => (
                <div key={i} className="border border-[#CCFF00]/20 bg-[#CCFF00]/[0.02] p-4 rounded-sm">
                  <div className="font-mono text-xs text-[#CCFF00] uppercase tracking-wider mb-1">{p.name} · {p.frequency}</div>
                  <div className="text-zinc-300 text-sm">{p.description}</div>
                </div>
              ))}
            </div>
          </Section>

          <Section title="Angles à voler" icon={Sparkles}>
            <div className="space-y-2">
              {(data.angles_to_steal || []).map((a, i) => (
                <div key={i} className="border border-[#1E1E24] bg-[#0A0A0C] p-4 rounded-sm">
                  <div className="text-white font-medium mb-1">{a.angle}</div>
                  <div className="text-xs text-zinc-500 mb-2">Pourquoi : {a.why}</div>
                  <div className="text-xs text-[#CCFF00]">→ {a.how_to_adapt}</div>
                </div>
              ))}
            </div>
          </Section>

          {data.weaknesses && (
            <Section title="Faiblesses détectées" icon={AlertTriangle}>
              <ul className="space-y-1">
                {data.weaknesses.map((w, i) => (
                  <li key={i} className="text-sm text-zinc-300 flex items-start gap-2">
                    <AlertTriangle className="w-3.5 h-3.5 text-orange-400 mt-1 shrink-0" />{w}
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {data.recommended_counter_strategy && (
            <div className="border-l-2 border-[#CCFF00] bg-[#CCFF00]/[0.04] p-5 rounded-sm">
              <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#CCFF00] mb-2 flex items-center gap-2">
                <Shield className="w-3 h-3" /> // CONTRE-STRATÉGIE
              </div>
              <div className="text-zinc-200 text-sm leading-relaxed">{data.recommended_counter_strategy}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const Card = ({ title, body, icon: I }) => (
  <div className="border border-[#1E1E24] bg-[#0A0A0C] p-5 rounded-sm">
    <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-2 flex items-center gap-2"><I className="w-3 h-3" /> {title}</div>
    <div className="text-white text-sm leading-relaxed">{body || "—"}</div>
  </div>
);

const Score = ({ label, value, color }) => (
  <div>
    <div className="text-[10px] font-mono uppercase text-zinc-500">{label}</div>
    <div className="font-mono text-3xl font-bold mt-1" style={{ color }}>{value || 0}<span className="text-zinc-600 text-sm">/100</span></div>
  </div>
);

const Section = ({ title, icon: I, children }) => (
  <section>
    <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-3 flex items-center gap-2">
      <I className="w-3 h-3" /> // {title.toUpperCase()}
    </div>
    {children}
  </section>
);
